import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { PlayArrow, ChevronLeft, ChevronRight, VolumeOff, VolumeUp } from '@mui/icons-material';
import { apiService } from '@services/api';
import { resolveCarouselMediaType } from '@/utils/carouselMedia';

type CarouselSlide = {
  url: string;
  filename: string;
  mediaType: 'video' | 'image';
};

function isValidMediaUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  return trimmed.startsWith('http://') || trimmed.startsWith('https://');
}

/**
 * Carrossel da landing do produto (mídia em R2: admin Produtos > Vídeos > Público).
 */
const LandingVideoCarousel: React.FC = () => {
  const [slides, setSlides] = useState<CarouselSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    apiService
      .getPublicVideos()
      .then((res) => {
        const data = (res.data?.data || []).filter(
          (v: { url?: string; filename?: string; mediaType?: string }) =>
            v?.url && isValidMediaUrl(v.url)
        ) as CarouselSlide[];
        setSlides(
          data.map((v) => ({
            url: v.url,
            filename: v.filename,
            mediaType: resolveCarouselMediaType(v.filename, v.mediaType),
          }))
        );
        if (data.length > 0) setActiveIndex(0);
        setLoadError(null);
      })
      .catch(() => {
        setSlides([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const goTo = useCallback(
    (delta: number) => {
      if (slides.length === 0) return;
      setActiveIndex((i) => (i + delta + slides.length) % slides.length);
      setIsPlaying(false);
      setLoadError(null);
    },
    [slides.length]
  );

  const setMediaLoadError = useCallback((kind: 'video' | 'image') => {
    setLoadError(
      kind === 'image'
        ? 'Não foi possível carregar a imagem.'
        : 'Não foi possível reproduzir o vídeo.'
    );
  }, []);

  const handlePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video || !isValidMediaUrl(video.src)) return;
    if (loadError) return;
    video.play().then(() => setIsPlaying(true)).catch(() => setMediaLoadError('video'));
  }, [loadError, setMediaLoadError]);

  const toggleMute = useCallback(() => {
    if (!videoRef.current) return;
    const next = !isMuted;
    videoRef.current.muted = next;
    setIsMuted(next);
  }, [isMuted]);

  useEffect(() => {
    setIsPlaying(false);
    setLoadError(null);
  }, [activeIndex]);

  useEffect(() => {
    const current = slides[activeIndex];
    if (!isPlaying && slides.length > 1 && !loadError && current?.mediaType === 'video') {
      const interval = setInterval(() => goTo(1), 5000);
      return () => clearInterval(interval);
    }
    if (!isPlaying && slides.length > 1 && !loadError && current?.mediaType === 'image') {
      const interval = setInterval(() => goTo(1), 8000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [isPlaying, slides, activeIndex, loadError, goTo]);

  if (loading || slides.length === 0) {
    return null;
  }

  const current = slides[activeIndex];
  if (!current) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: '#0d1a3a',
        border: '1px solid rgba(33, 49, 106, 0.2)',
        boxShadow: '0 12px 40px rgba(33, 49, 106, 0.15)',
        minHeight: { xs: 220, md: 320 },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        {current.mediaType === 'image' ? (
          <Box
            component="img"
            src={current.url}
            alt={current.filename}
            onError={() => setMediaLoadError('image')}
            sx={{
              width: '100%',
              display: 'block',
              maxHeight: 420,
              minHeight: { xs: 220, md: 300 },
              objectFit: 'contain',
              bgcolor: '#000',
            }}
          />
        ) : (
          <>
            <video
              ref={videoRef}
              src={current.url}
              preload="metadata"
              controls={isPlaying}
              playsInline
              muted={isMuted}
              onError={() => setMediaLoadError('video')}
              onPlay={() => {
                setIsPlaying(true);
                setLoadError(null);
              }}
              onPause={() => setIsPlaying(false)}
              onEnded={() => {
                if (slides.length > 1) goTo(1);
                else setIsPlaying(false);
              }}
              style={{
                width: '100%',
                display: 'block',
                maxHeight: 420,
                minHeight: 220,
                objectFit: 'contain',
                backgroundColor: '#000',
              }}
            />

            {!isPlaying && !loadError && (
              <Box
                onClick={handlePlay}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  background:
                    'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)',
                  transition: 'background 0.3s',
                  '&:hover': {
                    background:
                      'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.5) 100%)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 72, md: 96 },
                    height: { xs: 72, md: 96 },
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.95)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'scale(1.08)' },
                  }}
                >
                  <PlayArrow sx={{ fontSize: { xs: 40, md: 56 }, color: 'primary.main', ml: 0.5 }} />
                </Box>
              </Box>
            )}

            {isPlaying && (
              <IconButton
                onClick={toggleMute}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  bgcolor: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                }}
                size="small"
              >
                {isMuted ? <VolumeOff fontSize="small" /> : <VolumeUp fontSize="small" />}
              </IconButton>
            )}
          </>
        )}

        {loadError && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.8)',
              p: 2,
            }}
          >
            <Typography color="error" variant="body2" textAlign="center">
              {loadError}
            </Typography>
          </Box>
        )}

        {slides.length > 1 && (
          <>
            <IconButton
              onClick={() => goTo(-1)}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <ChevronLeft fontSize="large" />
            </IconButton>
            <IconButton
              onClick={() => goTo(1)}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <ChevronRight fontSize="large" />
            </IconButton>
          </>
        )}
      </Box>
      {slides.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            py: 1.5,
          }}
        >
          {slides.map((_, i) => (
            <Box
              key={i}
              onClick={() => setActiveIndex(i)}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: i === activeIndex ? 'white' : 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.8)' },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default LandingVideoCarousel;
